import logger from '@/utils/logger';
import { LOGIN_METHODS } from './constants';
import { hasEmbedUrlSession } from './embedUrlSession';
import { getLiveEmbedUrlSessionToken, getMagicToken } from './magicToken';
import { isTokenExpired } from './tokenDecode';
import { refreshViaAuth0Sdk, refreshViaRefreshToken } from './tokenRefresh';
import { getAccessToken, getLoginMethod, getRefreshToken, isEmbedMode } from './tokenStorage';
import type { TokenResolverDeps } from './types';

// gloss
let inflightResolve: Promise<string | null> | null = null;

// gloss
export const clearResolverCache = () => {
  inflightResolve = null;
};

// gloss
export const resolveToken = async (deps: TokenResolverDeps): Promise<string | null> => {
  if (hasEmbedUrlSession() && isEmbedMode()) return getLiveEmbedUrlSessionToken();

  const magicToken = getMagicToken();
  if (magicToken) return magicToken.token;

  const currentToken = getAccessToken();
  if (currentToken && !isTokenExpired(currentToken)) return currentToken;

  if (inflightResolve) return inflightResolve;

  inflightResolve = executeResolve(deps).finally(() => {
    inflightResolve = null;
  });

  return inflightResolve;
};

// gloss
const executeResolve = async (deps: TokenResolverDeps): Promise<string | null> => {
  const loginMethod = getLoginMethod();
  if (!loginMethod && !deps.isAuthenticated) return null;

  // why: always prefer the refresh_token grant over the Auth0 SDK iframe for every login method —
  // why: the iframe was the source of prior refresh-token errors.
  const hasRefreshToken = !!getRefreshToken();
  if (hasRefreshToken) {
    const result = await refreshViaRefreshToken();
    if (result.shouldLogout) {
      deps.onLogout?.();
      return null;
    }
    if (result.token) return result.token;
  }

  if ((loginMethod === LOGIN_METHODS.SERVICE || !loginMethod) && deps.isAuthenticated && deps.getAccessTokenSilently) {
    const result = await refreshViaAuth0Sdk(deps.getAccessTokenSilently, deps.isAuthenticated);
    if (result.shouldLogout) {
      deps.onLogout?.();
      return null;
    }
    if (result.token) return result.token;
  }

  return null;
};

// gloss
export const resolveAccessToken = async (fetchToken?: (() => Promise<string | null>) | null): Promise<string> => {
  if (typeof fetchToken === 'function') {
    try {
      const token = await fetchToken();
      if (token) return token;
    } catch (error) {
      logger.error(error, { context: 'resolveAccessToken' });
    }
  }

  throw new Error('Failed to get access token. Please try again.');
};
